import React, { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ProcessingState } from "@/components/ProcessingState";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatKr, formatDate, copyText } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, Camera, FileText, Image as ImageIcon, FileCode2, CheckCircle2, AlertTriangle, Copy, Mail, ArrowRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.xml,application/pdf,image/png,image/jpeg,image/webp,application/xml,text/xml";
const SOURCE_OPTIONS = [
  { value: "opplasting", label: "Opplasting (PDF / bilde)" },
  { value: "epost", label: "Mottatt på e-post" },
  { value: "efaktura", label: "eFaktura (lastet ned fra nettbank)" },
  { value: "vipps", label: "Vipps" },
  { value: "digipost", label: "Digipost" },
  { value: "eboks", label: "e-Boks" },
  { value: "papir", label: "Papirfaktura (fotografert)" },
  { value: "avtalegiro", label: "AvtaleGiro-varsel" },
];

export default function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [source, setSource] = useState("opplasting");
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]); // {id, file, status: 'venter'|'behandler'|'ferdig'|'feil', result, error}
  const processingRef = useRef(false);

  const processQueue = useCallback(async (items) => {
    if (processingRef.current) return;
    processingRef.current = true;
    for (const item of items) {
      setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "behandler" } : x)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("source", source);
        const res = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "ferdig", result: res.data } : x)));
        window.dispatchEvent(new Event("rp:refresh-notifications"));
      } catch (e) {
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "feil", error: errorMessage(e, "Kunne ikke laste opp") } : x)));
      }
    }
    processingRef.current = false;
  }, [source]);

  const addFiles = (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const tooBig = list.filter((f) => f.size > 15 * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.length} fil(er) er større enn 15 MB og ble hoppet over`);
    const ok = list.filter((f) => f.size <= 15 * 1024 * 1024);
    const items = ok.map((file) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, status: "venter" }));
    setQueue((q) => [...items, ...q]);
    processQueue(items);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const iconFor = (file) => {
    const n = file.name.toLowerCase();
    if (n.endsWith(".pdf")) return FileText;
    if (n.endsWith(".xml")) return FileCode2;
    return ImageIcon;
  };

  const done = queue.filter((q) => q.status === "ferdig");

  return (
    <div className="space-y-6">
      <PageHeader title="Last opp regning" description="PDF, bilde (JPG/PNG) eller EHF-fil. Vi leser leverandør, beløp, KID, kontonummer og forfallsdato automatisk og sjekker for duplikater." />

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="source">Hvor kommer regningen fra?</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger id="source" data-testid="upload-source-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{SOURCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="h-10 sm:hidden" onClick={() => cameraRef.current?.click()} data-testid="upload-camera-button"><Camera className="h-4 w-4 mr-2" /> Ta bilde</Button>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn("rounded-2xl border-2 border-dashed bg-card p-10 text-center cursor-pointer transition-colors", dragging && "dropzone-active")}
            data-testid="upload-dropzone"
            aria-label="Dra og slipp filer her eller klikk for å velge"
          >
            <div className="mx-auto h-14 w-14 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mb-4"><UploadCloud className="h-7 w-7" /></div>
            <p className="text-lg font-semibold">Dra og slipp filer her</p>
            <p className="text-sm text-muted-foreground mt-1">eller <span className="text-primary font-medium">velg filer</span> fra enheten din. Du kan laste opp flere samtidig.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><FileText className="h-3.5 w-3.5" /> PDF</span>
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><ImageIcon className="h-3.5 w-3.5" /> JPG / PNG / WEBP</span>
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><FileCode2 className="h-3.5 w-3.5" /> EHF / UBL XML</span>
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1">Maks 15 MB</span>
            </div>
            <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="upload-camera-input" />
          </div>

          {queue.length ? (
            <div className="space-y-3" data-testid="upload-queue">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Opplastinger ({queue.length})</h2>
                {done.length ? <Button variant="ghost" size="sm" onClick={() => setQueue((q) => q.filter((x) => x.status !== "ferdig"))}><X className="h-4 w-4 mr-1" /> Fjern ferdige</Button> : null}
              </div>
              {queue.map((item) => {
                const Icon = iconFor(item.file);
                return (
                  <Card key={item.id} data-testid={`upload-item-${item.status}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-muted-foreground" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.file.name}</p>
                          <p className="text-xs text-muted-foreground">{(item.file.size / 1024).toFixed(0)} KB</p>
                          {item.status === "venter" ? <p className="text-sm text-muted-foreground mt-2">Venter…</p> : null}
                          {item.status === "behandler" ? <div className="mt-3"><ProcessingState compact /></div> : null}
                          {item.status === "feil" ? <p className="text-sm text-destructive mt-2 flex items-center gap-1" data-testid="upload-error"><AlertTriangle className="h-4 w-4" /> {item.error}</p> : null}
                          {item.status === "ferdig" && item.result ? (
                            <div className="mt-3 rounded-lg border bg-secondary p-3" data-testid="upload-result">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <p className="font-medium">{item.result.issuer_name || "Ukjent avsender"}</p>
                                  <p className="text-sm text-muted-foreground tnum">{formatKr(item.result.amount, item.result.currency)} · forfall {formatDate(item.result.due_date)}{item.result.kid ? ` · KID ${item.result.kid}` : ""}</p>
                                </div>
                                <StatusBadge status={item.result.display_status || item.result.status} />
                              </div>
                              {item.result.status === "ma_kontrolleres" ? <p className="text-xs mt-2 text-review">Vi var usikre på noen felt – kontroller og bekreft.</p> : null}
                              {item.result.status === "mulig_duplikat" ? <p className="text-xs mt-2 text-dup">Ligner på en regning du allerede har.</p> : null}
                              {item.result.flags?.length ? <p className="text-xs mt-2 text-flag">{item.result.flags[0].title}</p> : null}
                              <div className="mt-3"><Button size="sm" onClick={() => navigate(`/fakturaer/${item.result.id}`)} data-testid="upload-open-invoice-button">Åpne faktura <ArrowRight className="h-4 w-4 ml-1" /></Button></div>
                            </div>
                          ) : null}
                        </div>
                        {item.status === "ferdig" ? <CheckCircle2 className="h-5 w-5 text-success shrink-0" /> : null}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          {user?.forwarding_address ? <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Videresend på e-post</CardTitle>
              <CardDescription>Får du fakturaer på e-post? Videresend dem til din personlige adresse, så leses vedleggene automatisk.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 rounded-lg border bg-secondary px-3 py-2">
                <code className="text-sm flex-1 truncate" data-testid="upload-forwarding-address">{user?.forwarding_address}</code>
                <Button size="sm" variant="outline" onClick={async () => { await copyText(user?.forwarding_address); toast.success("Adresse kopiert"); }} data-testid="upload-copy-forwarding"><Copy className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card> : null}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Har du ikke dokumentet?</CardTitle>
              <CardDescription>Registrer betalingsopplysningene manuelt – for eksempel fra eFaktura i nettbanken eller Vipps.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild className="w-full"><Link to="/registrer" data-testid="upload-go-manual">Registrer manuelt</Link></Button>
            </CardContent>
          </Card>
          <Card className="bg-secondary">
            <CardContent className="p-4 text-sm text-muted-foreground space-y-2">
              <p className="font-medium text-foreground">Slik fungerer det</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Dokumentet lagres kryptert og er kun tilgjengelig for deg.</li>
                <li>Feltene leses automatisk. Usikre felt merkes med lav sikkerhet.</li>
                <li>Vi sjekker for duplikater og endrede kontonummer.</li>
                <li>Du bekrefter, betaler i nettbanken og markerer som betalt.</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
